import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Node,
  Edge,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  NodeTypes,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Form, Select, Button, Card, Space, Modal, InputNumber } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  MobileOutlined,
  BellOutlined,
  MessageOutlined,
  MailOutlined,
  GlobalOutlined,
  WindowOutlined,
  WhatsAppOutlined,
  GoogleOutlined,
  AppstoreOutlined,
  PhoneOutlined,
  FileTextOutlined,
  RocketOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import pushNotificationIcon from "images/Push_new.svg";
import inAppNotificationIcon from "images/InApp_new.svg";
import webnewIcon from "images/Web_Push_new.svg"
import emailIcon from "images/Email_new.svg";
import webPopupIcon from "images/Web_Popup_new.svg";
import messageIcon from "images/SMS_new.svg";
import googleIcon from "images/Google.svg";
import whatsappIcon from "images/WhatsApp_new.svg";
import landingPageIcon from "images/Landing_Pg.svg";
import nativeDisplayIcon from "images/NativeDisplay.svg";
import rcsIcon from "images/RCS.svg";
import nudgeIcon from "images/Nudge.svg";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { setCampaignData } from "redux/features/campaign/campaignDataSlice";
import { setCurrentStep } from "redux/features/campaign/campaignCreateCurrentStepSlice";
import { fetchTemplatesList } from "redux/features/templates_/templatesByTypeSlice";
import { CampaignChannelTypeEnum } from "utils/enums";
import { v4 as uuid } from "uuid";
import { t } from "i18next";
import useMessage from "hooks/useMessage";
import "./CampaignFlow.css";

// Custom Node Component for Campaign Nodes
const CampaignNode = ({ data, selected, id }: any) => {
  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <div className={`campaign-flow-node ${selected ? "selected" : ""}`}>
      <Handle type="target" position={Position.Top} />
      <div className="campaign-node-header">
        <strong>{data.label}</strong>
        <Button
          type="text"
          size="small"
          icon={<CloseCircleOutlined />}
          onClick={onDelete}
          className="node-delete-btn"
          title={t("deleteNodeLabel") || "Delete Node"}
        />
      </div>
      <div className="campaign-node-body">
        <div className="campaign-node-type">{data.channel}</div>
        {data.templateName && (
          <div className="campaign-node-template">{data.templateName}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

// Custom Node Component for Time Window/Delay Node
const TimeWindowNode = ({ data, selected, id }: any) => {
  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <div className={`campaign-flow-node time-window-node ${selected ? "selected" : ""}`}>
      <Handle type="target" position={Position.Top} />
      <div className="campaign-node-header">
        <ClockCircleOutlined className="time-window-icon" />
        <strong>{data.label || t("timeWindowLabel") || "Time Window"}</strong>
        <Button
          type="text"
          size="small"
          icon={<CloseCircleOutlined />}
          onClick={onDelete}
          className="node-delete-btn"
          title={t("deleteNodeLabel") || "Delete Node"}
        />
      </div>
      <div className="campaign-node-body">
        {data.delay && (
          <div className="campaign-node-type">
            {data.delay} {data.unit || "minutes"}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

// Custom Node Component for Entry Node
const EntryNode = ({ data }: any) => {
  return (
    <div className="campaign-flow-node entry-node">
      <div className="campaign-node-header">
        <strong>Start</strong>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

// Custom Node Component for Exit Node
const ExitNode = ({ data }: any) => {
  return (
    <div className="campaign-flow-node exit-node">
      <Handle type="target" position={Position.Top} />
      <div className="campaign-node-header">
        <strong>End</strong>
      </div>
    </div>
  );
};

const nodeTypes: NodeTypes = {
  campaign: CampaignNode,
  timeWindow: TimeWindowNode,
  entry: EntryNode,
  exit: ExitNode,
};

// Separate component to use useReactFlow hook
const FlowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  nodeTypes,
  setNodes,
  setEdges,
  setSelectedNodeId,
  setIsModalVisible,
  setIsTimeWindowModalVisible,
  onChannelChange,
}: any) => {
  const { screenToFlowPosition } = useReactFlow();

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds: Node[]) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds: Edge[]) =>
        eds.filter(
          (edge) => edge.source !== nodeId && edge.target !== nodeId
        )
      );
    },
    [setNodes, setEdges]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const data = JSON.parse(
        event.dataTransfer.getData("application/reactflow")
      );

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (data.type === "campaign") {
        const newNodeId = uuid();
        const newNode: Node = {
          id: newNodeId,
          type: "campaign",
          position,
          data: {
            label: `Campaign ${nodes.filter((n) => n.type === "campaign").length + 1}`,
            channel: data.channel,
            templateId: undefined,
            templateName: undefined,
            onDelete: handleDeleteNode,
          },
        };
        setNodes((nds: Node[]) => [...nds, newNode]);
        setSelectedNodeId(newNodeId);
        // Fetch templates for the selected channel before opening modal
        if (data.channel && onChannelChange) {
          onChannelChange(data.channel);
        }
        setIsModalVisible(true);
      } else if (data.type === "timeWindow") {
        const newNodeId = uuid();
        const newNode: Node = {
          id: newNodeId,
          type: "timeWindow",
          position,
          data: {
            label: t("timeWindowLabel") || "Time Window",
            delay: undefined,
            unit: "minutes",
            onDelete: handleDeleteNode,
          },
        };
        setNodes((nds: Node[]) => [...nds, newNode]);
        setSelectedNodeId(newNodeId);
        setIsTimeWindowModalVisible(true);
      }
    },
    [
      screenToFlowPosition,
      nodes,
      setNodes,
      setSelectedNodeId,
      setIsModalVisible,
      setIsTimeWindowModalVisible,
      handleDeleteNode,
      onChannelChange,
    ]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      onDrop={onDrop}
      onDragOver={onDragOver}
      fitView
    >
      <Background />
      <Controls />
      <MiniMap />
    </ReactFlow>
  );
};

const CampaignFlow: React.FC = () => {
  const dispatch: any = useDispatch();
  const campaignData = useSelector((state: any) => state.campaignData.data);
  const currentStep = useSelector(
    (state: any) => state.campaignCreateCurrentStep
  );
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const { showError, showSuccess } = useMessage();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTimeWindowModalVisible, setIsTimeWindowModalVisible] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [timeWindowForm] = Form.useForm();

  const features = useSelector((state: any) => state.featuresSetting.data);
  const channelStatus = features[0]?.channel_status;

  const channelOptions = useMemo(() => {
    const allChannels = Object.values(CampaignChannelTypeEnum);
    if (!channelStatus) {
      return allChannels
        .filter((key) => key !== CampaignChannelTypeEnum.ALL && key !== CampaignChannelTypeEnum.FACEBOOK)
        .map((key) => ({ value: key, label: key }));
    }
    return allChannels
      .filter(
        (key) =>
          key !== CampaignChannelTypeEnum.ALL &&
          key !== CampaignChannelTypeEnum.FACEBOOK &&
          (key === CampaignChannelTypeEnum.SMS || channelStatus[key] === true) &&
          key !== CampaignChannelTypeEnum.MULTI_STEP &&
          key !== CampaignChannelTypeEnum.CANVAS
      )
      .map((key) => ({ value: key, label: key }));
  }, [channelStatus]);

  // Initialize nodes and edges
  const initialNodes: Node[] = [
    {
      id: "entry",
      type: "entry",
      position: { x: 250, y: 50 },
      data: { label: "Start" },
    },
    {
      id: "exit",
      type: "exit",
      position: { x: 250, y: 600 },
      data: { label: "End" },
    },
  ];

  const initialEdges: Edge[] = [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // handleDeleteNode will be defined later, but we'll use it in useEffect
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds) =>
        eds.filter(
          (edge) => edge.source !== nodeId && edge.target !== nodeId
        )
      );
    },
    [setNodes, setEdges]
  );

  // Load existing canvas data if editing
  useEffect(() => {
    // Load from flowData (stored directly on campaign document, like multiStepData)
    const flowData = campaignData?.flowData;
    
    if (flowData) {
      const { nodes: savedNodes, edges: savedEdges } = flowData;
      if (savedNodes && savedEdges && Array.isArray(savedNodes) && Array.isArray(savedEdges)) {
        // Restore onDelete handlers when loading nodes from Redux
        const nodesWithHandlers = savedNodes.map((node: any) => ({
          ...node,
          data: {
            ...node.data,
            onDelete: handleDeleteNode,
          },
        }));
        setNodes(nodesWithHandlers);
        setEdges(savedEdges);
      }
    }
  }, [campaignData?.flowData, setNodes, setEdges, handleDeleteNode]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  const addCampaignNode = useCallback(() => {
    const newNodeId = uuid();
    const newNode: Node = {
      id: newNodeId,
      type: "campaign",
      position: {
        x: Math.random() * 400 + 100,
        y: Math.random() * 300 + 150,
      },
      data: {
        label: `Campaign ${nodes.filter((n) => n.type === "campaign").length + 1}`,
        channel: undefined,
        templateId: undefined,
        templateName: undefined,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNodeId(newNodeId);
    setIsModalVisible(true);
  }, [nodes, setNodes]);

  const updateNodeData = (nodeId: string, data: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      )
    );
  };

  const handleChannelChange = async (channel: string) => {
    if (!channel) {
      setTemplates([]);
      form.setFieldsValue({ templateId: undefined });
      return;
    }

    setLoadingTemplates(true);
    try {
      const action = await dispatch(
        fetchTemplatesList({
          app_id: appId,
          api_key: loginUser.api_key,
          template_type: channel as CampaignChannelTypeEnum,
        })
      );
      
      // fetchTemplatesList thunk returns action object with payload
      // The payload contains the templates array (result from API)
      const templatesList = action.payload || [];
      setTemplates(Array.isArray(templatesList) ? templatesList : []);
      
      if (!Array.isArray(templatesList) || templatesList.length === 0) {
        console.warn("No templates found for channel:", channel);
      }
    } catch (error: any) {
      console.error("Failed to fetch templates:", error);
      showError(error?.message || t("failedToFetchTemplatesLabel") || "Failed to fetch templates");
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Get selected node based on selectedNodeId
  const selectedNode = useMemo(() => {
    return nodes.find((node) => node.id === selectedNodeId);
  }, [nodes, selectedNodeId]);

  // Fetch templates when modal opens if channel is already set
  useEffect(() => {
    if (isModalVisible && selectedNode?.data?.channel) {
      handleChannelChange(selectedNode.data.channel);
    }
  }, [isModalVisible, selectedNode?.data?.channel]);

  const handleNodeConfig = (values: any) => {
    if (selectedNodeId && selectedNode) {
      const selectedTemplate = templates.find(
        (t) => t._id === values.templateId
      );
      // Channel is already set from drag, don't change it
      updateNodeData(selectedNodeId, {
        channel: selectedNode.data.channel, // Keep the original channel
        templateId: values.templateId,
        templateName: selectedTemplate?.template_name || selectedNode.data.channel,
        onDelete: handleDeleteNode,
      });
      setIsModalVisible(false);
      setSelectedNodeId(null);
      form.resetFields();
      setTemplates([]);
    }
  };

  const handleTimeWindowConfig = (values: any) => {
    if (selectedNodeId) {
      updateNodeData(selectedNodeId, {
        delay: values.delay,
        unit: values.unit || "minutes",
        onDelete: handleDeleteNode,
      });
      setIsTimeWindowModalVisible(false);
      setSelectedNodeId(null);
      timeWindowForm.resetFields();
    }
  };


  // Convert ReactFlow structure to payload format (similar to multistep)
  const convertToPayloadStructure = () => {
    const campaignNodes = nodes.filter((node) => node.type === "campaign");
    const entryNode = nodes.find((node) => node.type === "entry");
    const exitNode = nodes.find((node) => node.type === "exit");

    if (campaignNodes.length === 0) {
      throw new Error("Please add at least one campaign node");
    }

    // Build the flow structure similar to multistep
    const flowStructure = {
      entryNode: {
        type: "EntryNode",
        segment: uuid(),
        child: null,
      },
      exitNode: {
        type: "ExitNode",
      },
      nodes: campaignNodes.map((node) => ({
        id: node.id,
        type: "MessageNode",
        channel: node.data.channel,
        templateId: node.data.templateId,
        label: node.data.label,
        child: null,
      })),
      edges: edges.map((edge) => ({
        source: edge.source,
        target: edge.target,
        id: edge.id,
      })),
    };

    // Connect nodes based on edges
    edges.forEach((edge) => {
      const sourceNode = flowStructure.nodes.find(
        (n: any) => n.id === edge.source
      );
      if (sourceNode) {
        if (edge.target === "exit") {
          sourceNode.child = "ExitNode";
        } else {
          sourceNode.child = edge.target;
        }
      }
    });

    // Set entry node's child to first campaign node
    if (campaignNodes.length > 0) {
      const firstCampaignEdge = edges.find((e) => e.source === "entry");
      if (firstCampaignEdge) {
        flowStructure.entryNode.child = firstCampaignEdge.target;
      }
    }

    return flowStructure;
  };

  const onFormSubmit = async () => {
    try {
      const campaignNodes = nodes.filter((node) => node.type === "campaign");
      
      if (campaignNodes.length === 0) {
        showError("Please add at least one campaign node");
        return;
      }

      // Validate all campaign nodes have channel and template
      const invalidNodes = campaignNodes.filter(
        (node) => !node.data.channel || !node.data.templateId
      );

      if (invalidNodes.length > 0) {
        showError("Please configure all campaign nodes with channel and template");
        return;
      }

      // Convert to payload structure
      const flowStructure = convertToPayloadStructure();

      // Remove non-serializable values (functions like onDelete) from nodes before saving
      const serializableNodes = nodes.map((node) => {
        const { onDelete, ...nodeData } = node.data || {};
        return {
          ...node,
          data: nodeData,
        };
      });

      // Save to Redux only (like MultiStep)
      // flowData and flowStructure will be saved directly to campaign document in LaunchCampaign step
      const updatedCampaignData = {
        ...campaignData,
        flowData: {
          nodes: serializableNodes,
          edges,
        },
        flowStructure,
        t: CampaignChannelTypeEnum.CANVAS,
      };

      dispatch(setCampaignData(updatedCampaignData));
      showSuccess("Canvas saved successfully");
      dispatch(setCurrentStep(currentStep + 1));
    } catch (error: any) {
      showError(error.message || t("somethingWrongLabel"));
    }
  };


  return (
    <div className="campaign-flow-container">
      <div className="campaign-flow-header">
        <h3>{t("canvasLabel") || "Canvas"}</h3>
        <PrimaryButton onClick={onFormSubmit} style={{ height: '32px', padding: '0 16px' }}>
          {t("saveLabel")}
        </PrimaryButton>
      </div>

      <div className="campaign-flow-content">
        {/* Left Sidebar with Draggable Nodes */}
        <div className="campaign-flow-sidebar">
          <div className="sidebar-nodes">
            <div className="sidebar-section">
              <div className="sidebar-section-title">{t("campaignTypesLabel") || "Campaign Types"}</div>
              {channelOptions.map((channel) => {
                const getChannelIcon = (channelType: string) => {
                  const iconMap: { [key: string]: any } = {
                    PUSH: <img src={pushNotificationIcon} alt="Push" className="sidebar-icon-img" />,
                    "IN-APP": <img src={inAppNotificationIcon} alt="In-App" className="sidebar-icon-img" />,
                    SMS: <img src={messageIcon} alt="SMS" className="sidebar-icon-img" />,
                    EMAIL: <img src={emailIcon} alt="Email" className="sidebar-icon-img" />,
                    WEB_PUSH: <img src={webnewIcon} alt="Web Push" className="sidebar-icon-img" />,
                    WEB_POPUP: <img src={webPopupIcon} alt="Web Popup" className="sidebar-icon-img" />,
                    WHATSAPP: <img src={whatsappIcon} alt="WhatsApp" className="sidebar-icon-img" />,
                    GOOGLE: <img src={googleIcon} alt="Google" className="sidebar-icon-img" />,
                    NATIVE: <img src={nativeDisplayIcon} alt="Native" className="sidebar-icon-img" />,
                    RCS: <img src={rcsIcon} alt="RCS" className="sidebar-icon-img" />,
                    LANDING_PAGE: <img src={landingPageIcon} alt="Landing Page" className="sidebar-icon-img" />,
                    NUDGE: <img src={nudgeIcon} alt="Nudge" className="sidebar-icon-img" />,
                  };
                  return iconMap[channelType] || <MailOutlined className="sidebar-icon" />;
                };

                return (
                  <div
                    key={channel.value}
                    className="sidebar-node-item"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/reactflow", JSON.stringify({
                        type: "campaign",
                        channel: channel.value,
                      }));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <div className="sidebar-node-icon">
                      {getChannelIcon(channel.value)}
                    </div>
                    <div className="sidebar-node-label">{channel.label}</div>
                  </div>
                );
              })}
            </div>
            
            <div className="sidebar-section">
              <div className="sidebar-section-title">{t("flowControlsLabel") || "Flow Controls"}</div>
              <div
                className="sidebar-node-item time-window-item"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/reactflow", JSON.stringify({
                    type: "timeWindow",
                  }));
                  e.dataTransfer.effectAllowed = "move";
                }}
              >
                <div className="sidebar-node-icon">
                  <ClockCircleOutlined className="sidebar-icon" />
                </div>
                <div className="sidebar-node-label">{t("timeWindowLabel") || "Time Window"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Canvas */}
        <div className="campaign-flow-canvas">
          <ReactFlowProvider>
            <FlowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              setNodes={setNodes}
              setEdges={setEdges}
              setSelectedNodeId={setSelectedNodeId}
              setIsModalVisible={setIsModalVisible}
              setIsTimeWindowModalVisible={setIsTimeWindowModalVisible}
              onChannelChange={handleChannelChange}
            />
          </ReactFlowProvider>
        </div>
      </div>

      {/* Campaign Node Configuration Modal */}
      <Modal
        title={
          <div>
            {t("configureCampaignNodeLabel") || "Configure Campaign Node"}
            {selectedNode?.data?.channel && (
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                ({selectedNode.data.channel})
              </span>
            )}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedNodeId(null);
          form.resetFields();
          setTemplates([]);
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleNodeConfig} layout="vertical">
          <Form.Item
            name="templateId"
            label={t("templateLabel") || "Template"}
            rules={[{ required: true, message: t("pleaseSelectTemplateLabel") || "Please select a template" }]}
            initialValue={selectedNode?.data?.templateId}
          >
            <Select
              placeholder={t("selectTemplateLabel") || "Select template"}
              showSearch
              allowClear
              loading={loadingTemplates}
              options={templates.map((template) => ({
                value: template._id,
                label: template.template_name || template._id,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t("saveLabel") || "Save"}
              </Button>
              <Button
                onClick={() => {
                  if (selectedNodeId) {
                    handleDeleteNode(selectedNodeId);
                    setIsModalVisible(false);
                    setSelectedNodeId(null);
                    form.resetFields();
                    setTemplates([]);
                  }
                }}
                danger
                icon={<DeleteOutlined />}
              >
                {t("deleteNodeLabel") || "Delete Node"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Time Window Configuration Modal */}
      <Modal
        title={t("configureTimeWindowLabel") || "Configure Time Window"}
        open={isTimeWindowModalVisible}
        onCancel={() => {
          setIsTimeWindowModalVisible(false);
          setSelectedNodeId(null);
          timeWindowForm.resetFields();
        }}
        footer={null}
      >
        <Form form={timeWindowForm} onFinish={handleTimeWindowConfig} layout="vertical">
          <Form.Item
            name="delay"
            label={t("delayTimeLabel") || "Delay Time"}
            rules={[{ required: true, message: t("pleaseEnterDelayLabel") || "Please enter delay time" }]}
            initialValue={selectedNode?.data?.delay}
          >
            <InputNumber
              min={1}
              placeholder={t("enterDelayLabel") || "Enter delay"}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="unit"
            label={t("timeUnitLabel") || "Time Unit"}
            initialValue={selectedNode?.data?.unit || "minutes"}
          >
            <Select
              options={[
                { value: "minutes", label: t("minutesLabel") || "Minutes" },
                { value: "hours", label: t("hoursLabel") || "Hours" },
                { value: "days", label: t("daysLabel") || "Days" },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t("saveLabel") || "Save"}
              </Button>
              <Button
                onClick={() => {
                  if (selectedNodeId) {
                    handleDeleteNode(selectedNodeId);
                    setIsTimeWindowModalVisible(false);
                    setSelectedNodeId(null);
                    timeWindowForm.resetFields();
                  }
                }}
                danger
                icon={<DeleteOutlined />}
              >
                {t("deleteNodeLabel") || "Delete Node"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CampaignFlow;
